import { useEffect, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'

import DropdownIcon from '../../../../app/components/DropdownIcon'
import PopoverPanel from '../../../../app/components/popover/PopoverPanel'
import usePopover from '../../../../app/hooks/usePopover'
import sessionStorageUtils from '../../../../app/utils/sessionStorageUtils'
import ProfileAvatar from '../../../../company/components/ProfileAvatar'
import ProfileSwitcherOptions from '../../../../company/components/ProfileSwitcherOptions'
import useProfileOptions from '../../../../company/hooks/useProfileOptions'
import useShowCompanyProfileNudge from '../../../../company/hooks/useShowCompanyProfileNudge'

import styles from './NavbarWorkspaceSwitcher.module.scss'

/**
 * "Ordering for" switcher: always shows whether the user orders
 * privately or for a company (and which), and switches in two clicks
 */
export default function NavbarWorkspaceSwitcher() {
  const { t } = useTranslation('common')

  const profileOptions = useProfileOptions()
  const { isOpen, close, containerRef, triggerRef, triggerProps, popoverId } =
    usePopover()

  const currentProfile =
    profileOptions.find(option => option.isCurrent) ?? profileOptions[0]

  const isCompany = currentProfile.isCompany

  // A private user with a company profile gets a dot until they open the
  // switcher once this browser session. Read after mount (SSR).
  const showCompanyNudge = useShowCompanyProfileNudge()
  const [isNudgeSeen, setIsNudgeSeen] = useState(true)

  useEffect(() => {
    setIsNudgeSeen(!!sessionStorageUtils.getItem<boolean>('companyNudgeSeen'))
  }, [])

  useEffect(() => {
    if (isOpen) {
      sessionStorageUtils.setItem('companyNudgeSeen', true)
      setIsNudgeSeen(true)
    }
  }, [isOpen])

  const showNudgeDot = showCompanyNudge && !isNudgeSeen

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className={clsx(styles.trigger, {
          [styles.company]: isCompany,
          [styles.isOpen]: isOpen,
        })}
        aria-label={
          showNudgeDot
            ? t('companyNudge.switcherAria', { name: currentProfile.name })
            : t('identity.changeProfileAria', { name: currentProfile.name })
        }
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...triggerProps}
      >
        <span className={styles.avatarWrapper}>
          <ProfileAvatar
            name={currentProfile.avatarName}
            isCompany={isCompany}
            size={isCompany ? 'large' : 'medium'}
            className={styles.avatar}
          />
          {showNudgeDot && <span className={styles.nudgeDot} aria-hidden />}
        </span>
        <span className={styles.text}>
          <span className={styles.label}>{t('identity.orderingFor')}</span>
          <span className={styles.name}>{currentProfile.name}</span>
        </span>
        <DropdownIcon isOpen={isOpen} className={styles.chevron} />
      </button>
      {isOpen && (
        <PopoverPanel id={popoverId} align="start">
          <ProfileSwitcherOptions onDone={close} />
        </PopoverPanel>
      )}
    </div>
  )
}
